/**
 * Watering Schedule Calculator
 * Calculates optimal watering schedules based on grass type, soil, and irrigation system
 */

import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { IRRIGATION_SYSTEMS } from "@/data/watering-requirements"
import { getCurrentSeason } from "./date-utils"
import {
  adjustWaterNeedForSunlight,
  attachToolEngineMetadata,
  buildCanonicalToolContext,
  gallonsForInches,
  type DeterministicToolResultMetadata,
} from "@/lib/lawn-plan-engine"

export interface WateringScheduleResult extends DeterministicToolResultMetadata {
  grassType: string
  lawnSize: number // sq ft
  zoneCount: number
  averageZoneSize: number // sq ft
  soilType: string
  irrigationSystem: string
  season: 'spring' | 'summer' | 'fall' | 'winter'
  
  // Calculated values
  weeklyWaterNeeded: number // inches per week
  soilAdjustedWater: number // inches per week (adjusted for soil type)
  weeklyGallons: number // gallons per week for entire lawn
  weeklyGallonsPerZone: number // gallons per week per average zone
  gallonsPerZonePerSession: number // gallons per average zone per watering session
  targetInchesPerSession: number // inches applied during each watering event
  defaultApplicationRateInchesPerHour: number // system estimate after efficiency
  applicationRateInchesPerHour: number // rate used for the headline runtime
  applicationRateSource: ApplicationRateSource
  catchCanTestMinutes?: number

  // Rainfall adjustment
  rainfallSubtracted: number // inches of recent rainfall subtracted from need
  skipThisWeek: boolean // true if rainfall meets/exceeds weekly need

  // Schedule details
  irrigationFrequency: string
  sessionDuration: number // average minutes per zone/coverage area per session
  sessionsPerWeek: number
  controllerRunMinutesPerSession: number // total minutes if zones run one after another
  controllerRunMinutesPerWeek: number
  zoneRunTimes: ZoneWateringRuntime[]
  cycleAndSoak: CycleAndSoakRecommendation
  
  // Cost analysis
  weeklyCost?: number // dollars per week
  monthlyCost?: number // dollars per month
  seasonalCost?: number // dollars per season
  
  // Recommendations
  seasonalAdvice: string
  waterConservationTips: string[]
  systemOptimizationTips: string[]
  specialConsiderations: string[]
}

export type ApplicationRateSource =
  | 'system-estimate'
  | 'catch-can-average'
  | 'catch-can-by-zone'

export interface WateringCalibrationInput {
  testMinutes?: number
  catchCanDepthInches?: number
  zoneCatchCanDepthsInches?: Array<number | null | undefined>
}

export interface ZoneWateringRuntime {
  zoneNumber: number
  precipitationRateInchesPerHour: number
  runMinutesPerSession: number
  runMinutesPerWeek: number
  gallonsPerSession: number
  gallonsPerWeek: number
  rateSource: 'system-estimate' | 'catch-can'
}

export interface CycleAndSoakRecommendation {
  recommended: boolean
  cycles: number
  runMinutesPerCycle: number
  soakMinutes: number
  maxZoneRunMinutes: number
  reason: string
}

const MIN_ZONE_COUNT = 1
const MAX_ZONE_COUNT = 24
const MIN_CATCH_CAN_MINUTES = 5
const MAX_CATCH_CAN_MINUTES = 60
const MIN_APPLICATION_RATE = 0.05
const MAX_APPLICATION_RATE = 12

function normalizeZoneCount(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return MIN_ZONE_COUNT
  return Math.max(MIN_ZONE_COUNT, Math.min(MAX_ZONE_COUNT, Math.round(value)))
}

function normalizeCatchCanMinutes(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 15
  return Math.max(MIN_CATCH_CAN_MINUTES, Math.min(MAX_CATCH_CAN_MINUTES, value))
}

function toApplicationRate(depthInches: number | null | undefined, testMinutes: number): number | null {
  if (typeof depthInches !== 'number' || !Number.isFinite(depthInches) || depthInches <= 0) {
    return null
  }

  const rate = depthInches * (60 / testMinutes)
  return Math.max(MIN_APPLICATION_RATE, Math.min(MAX_APPLICATION_RATE, rate))
}

function round1(value: number): number {
  return parseFloat(value.toFixed(1))
}

function roundRate(value: number): number {
  return parseFloat(value.toFixed(value < 0.1 ? 2 : 1))
}

function round0(value: number): number {
  return parseFloat(value.toFixed(0))
}

function buildCycleAndSoakRecommendation({
  irrigationSystemName,
  soilType,
  applicationRateInchesPerHour,
  zoneRunTimes,
}: {
  irrigationSystemName: string
  soilType: 'clay' | 'sandy' | 'loamy' | 'unknown'
  applicationRateInchesPerHour: number
  zoneRunTimes: ZoneWateringRuntime[]
}): CycleAndSoakRecommendation {
  const maxZoneRunMinutes = zoneRunTimes.length > 0
    ? Math.max(...zoneRunTimes.map((zone) => zone.runMinutesPerSession))
    : 0

  if (maxZoneRunMinutes <= 0) {
    return {
      recommended: false,
      cycles: 0,
      runMinutesPerCycle: 0,
      soakMinutes: 0,
      maxZoneRunMinutes: 0,
      reason: 'No watering is scheduled.',
    }
  }

  const lowVolumeSystem = irrigationSystemName === 'Drip Irrigation' || irrigationSystemName === 'Soaker Hose'
  if (lowVolumeSystem) {
    return {
      recommended: false,
      cycles: 1,
      runMinutesPerCycle: Math.ceil(maxZoneRunMinutes),
      soakMinutes: 0,
      maxZoneRunMinutes: round1(maxZoneRunMinutes),
      reason: 'Low-volume irrigation usually applies water slowly enough to run in one pass.',
    }
  }

  const sprayLike = applicationRateInchesPerHour >= 1
  const baseCycleLimit = sprayLike ? 16 : 48
  const cycleLimit = soilType === 'clay'
    ? Math.min(baseCycleLimit, sprayLike ? 12 : 30)
    : baseCycleLimit
  const cycles = maxZoneRunMinutes > cycleLimit
    ? Math.min(3, Math.ceil(maxZoneRunMinutes / cycleLimit))
    : 1

  return {
    recommended: cycles > 1,
    cycles,
    runMinutesPerCycle: Math.ceil(maxZoneRunMinutes / cycles),
    soakMinutes: cycles > 1 ? 30 : 0,
    maxZoneRunMinutes: round1(maxZoneRunMinutes),
    reason: cycles > 1
      ? soilType === 'clay'
        ? 'Clay soil absorbs water slowly, so splitting the runtime helps reduce runoff.'
        : 'Long sprinkler runtimes are easier for the soil to absorb when split into shorter cycles.'
      : 'The per-zone runtime is short enough to run in one pass for most lawns.',
  }
}

export function calculateWateringSchedule(
  grassType: string,
  lawnSize: number | string,
  soilType: 'clay' | 'sandy' | 'loamy' | 'unknown',
  irrigationSystemName: string,
  selectedSeason?: 'spring' | 'summer' | 'fall' | 'winter',
  costPerGallon?: number,
  waterRestrictions?: boolean,
  recentRainfallInches: number = 0,
  irrigationZoneCount: number = 1,
  calibration?: WateringCalibrationInput,
  sunlight?: string | null
): WateringScheduleResult {
  const context = buildCanonicalToolContext({
    toolId: 'watering',
    grassType,
    lawnSize,
    sunlight,
    hasWeather: true,
  })
  const canonicalLawnSize = context.lawnSizeSqft ?? Number.NaN
  const grass = GRASS_CHARACTERISTICS[context.grassType]
  const irrigationSystem = IRRIGATION_SYSTEMS[irrigationSystemName]

  if (!grass || !grass.wateringNeeds) {
    throw new Error('Watering data is not available for this grass type.')
  }

  if (!irrigationSystem) {
    throw new Error('Unsupported irrigation system. Please choose one from the list.')
  }

  const currentSeason = selectedSeason || getCurrentSeason()
  const zoneCount = normalizeZoneCount(irrigationZoneCount)

  // Get base water requirements for the season
  const baseWeeklyWaterNeeded = grass.wateringNeeds.weeklyRequirements[currentSeason]
  if (baseWeeklyWaterNeeded === undefined) {
    throw new Error('Watering details are not available for this grass type and season.')
  }
  const weeklyWaterNeeded = adjustWaterNeedForSunlight(baseWeeklyWaterNeeded, context.sunlight)

  // Compute rainfall context for the banner / downstream consumers. The
  // schedule fields below always reflect the user's typical weekly pattern
  // (the durable plan), not a one-week adjustment. `skipThisWeek` and
  // `rainfallSubtracted` are the signals callers use to surface "skip this
  // week" or "we adjusted this week" callouts above the baseline schedule.
  const safeRainfall = Math.max(0, recentRainfallInches || 0)
  const rainfallSubtracted = Math.min(weeklyWaterNeeded, safeRainfall)
  const skipThisWeek = rainfallSubtracted >= weeklyWaterNeeded

  // Apply soil type adjustments
  let soilMultiplier = 1.0
  switch (soilType) {
    case 'clay':
      soilMultiplier = 0.8 // Clay holds water longer
      break
    case 'sandy':
      soilMultiplier = 1.3 // Sandy drains quickly
      break
    case 'loamy':
      soilMultiplier = 1.0 // Loamy is ideal
      break
    case 'unknown':
      soilMultiplier = 1.0 // Default to ideal
      break
  }

  const soilAdjustedWater = weeklyWaterNeeded * soilMultiplier
  
  // Convert inches to gallons for the lawn size
  // 1 inch of water = 0.623 gallons per square foot
  const weeklyGallons = gallonsForInches(canonicalLawnSize, soilAdjustedWater)
  
  // Calculate irrigation frequency based on system and grass needs
  let sessionsPerWeek = 3 // Default
  switch (irrigationSystemName) {
    case 'Sprinkler System':
      sessionsPerWeek = currentSeason === 'summer' ? 4 : 3
      break
    case 'Spray Heads':
      sessionsPerWeek = currentSeason === 'summer' ? 4 : 3
      break
    case 'Rotor Heads':
      sessionsPerWeek = currentSeason === 'summer' ? 3 : 2
      break
    case 'Oscillating Sprinkler':
      sessionsPerWeek = currentSeason === 'summer' ? 4 : 3
      break
    case 'Rotary Sprinkler':
      sessionsPerWeek = currentSeason === 'summer' ? 3 : 2
      break
    case 'Soaker Hose':
      sessionsPerWeek = currentSeason === 'summer' ? 5 : 4
      break
    case 'Hose and Sprayer':
      sessionsPerWeek = currentSeason === 'summer' ? 3 : 2
      break
    case 'Drip Irrigation':
      sessionsPerWeek = currentSeason === 'summer' ? 7 : 5
      break
  }
  
  // Adjust for grass type
  if (grass.wateringNeeds.droughtTolerance === 'High') {
    sessionsPerWeek = Math.max(2, sessionsPerWeek - 1)
  } else if (grass.wateringNeeds.droughtTolerance === 'Low') {
    sessionsPerWeek = Math.min(5, sessionsPerWeek + 1)
  }

  // Universal "deep and infrequent" cap. Drip can run more often by design,
  // everything else stays at 4/week max so summer schedules don't contradict
  // the tool's own conservation guidance.
  const isDrip = irrigationSystemName === 'Drip Irrigation'
  const maxSessions = isDrip ? 5 : 4
  sessionsPerWeek = Math.min(sessionsPerWeek, maxSessions)

  // Dormancy override: when the grass has no weekly water need in this season
  // (warm-season grasses in winter), there is no schedule. Force sessions to
  // 0 so the UI stays internally consistent with the "No watering needed
  // this season" banner — otherwise we'd surface "0 gal but 3× per week".
  // This is distinct from a rain-skip week, where weeklyWaterNeeded > 0 and
  // the typical schedule should still render below the skip banner.
  if (weeklyWaterNeeded <= 0) {
    sessionsPerWeek = 0
  }

  // System estimates include the built-in efficiency adjustment. Catch-can
  // measurements replace this with the actual precipitation rate seen in cans.
  const defaultEffectiveRate = irrigationSystem.applicationRate * irrigationSystem.efficiency
  const targetInchesPerSession = sessionsPerWeek > 0 ? soilAdjustedWater / sessionsPerWeek : 0
  const averageZoneSize = canonicalLawnSize / zoneCount
  const weeklyGallonsPerZone = weeklyGallons / zoneCount
  const gallonsPerZonePerSession = sessionsPerWeek > 0 ? weeklyGallonsPerZone / sessionsPerWeek : 0
  const catchCanTestMinutes = normalizeCatchCanMinutes(calibration?.testMinutes)
  const averageCatchCanRate = toApplicationRate(calibration?.catchCanDepthInches, catchCanTestMinutes)
  const measuredZoneRates = calibration?.zoneCatchCanDepthsInches
    ?.slice(0, zoneCount)
    .map((depth) => toApplicationRate(depth, catchCanTestMinutes)) ?? []
  const hasZoneMeasurements = measuredZoneRates.some((rate) => rate !== null)

  const applicationRateSource: ApplicationRateSource = hasZoneMeasurements
    ? 'catch-can-by-zone'
    : averageCatchCanRate
      ? 'catch-can-average'
      : 'system-estimate'

  const zoneRates = Array.from({ length: zoneCount }, (_, index) => {
    if (applicationRateSource === 'catch-can-by-zone') {
      return {
        rate: measuredZoneRates[index] ?? averageCatchCanRate ?? defaultEffectiveRate,
        source: measuredZoneRates[index] ? 'catch-can' as const : 'system-estimate' as const,
      }
    }

    if (applicationRateSource === 'catch-can-average' && averageCatchCanRate) {
      return { rate: averageCatchCanRate, source: 'catch-can' as const }
    }

    return { rate: defaultEffectiveRate, source: 'system-estimate' as const }
  })

  const zoneRunTimes = zoneRates.map(({ rate, source }, index): ZoneWateringRuntime => {
    const runMinutesPerSession = sessionsPerWeek > 0 && rate > 0
      ? round1((targetInchesPerSession / rate) * 60)
      : 0

    return {
      zoneNumber: index + 1,
      precipitationRateInchesPerHour: roundRate(rate),
      runMinutesPerSession,
      runMinutesPerWeek: round1(runMinutesPerSession * sessionsPerWeek),
      gallonsPerSession: round0(gallonsPerZonePerSession),
      gallonsPerWeek: round0(weeklyGallonsPerZone),
      rateSource: source,
    }
  })

  const controllerRunMinutesPerSession = round1(
    zoneRunTimes.reduce((total, zone) => total + zone.runMinutesPerSession, 0)
  )
  const controllerRunMinutesPerWeek = round1(controllerRunMinutesPerSession * sessionsPerWeek)
  const roundedSessionDuration = zoneRunTimes.length > 0
    ? round1(controllerRunMinutesPerSession / zoneRunTimes.length)
    : 0
  const applicationRateInchesPerHour = zoneRates.length > 0
    ? roundRate(zoneRates.reduce((total, zone) => total + zone.rate, 0) / zoneRates.length)
    : roundRate(defaultEffectiveRate)
  const cycleAndSoak = buildCycleAndSoakRecommendation({
    irrigationSystemName,
    soilType,
    applicationRateInchesPerHour,
    zoneRunTimes,
  })
  
  // Get irrigation frequency description
  const irrigationFrequency = grass.wateringNeeds.irrigationFrequency[currentSeason] || 'As needed based on soil moisture'
  
  // Calculate costs if provided
  let weeklyCost: number | undefined
  let monthlyCost: number | undefined
  let seasonalCost: number | undefined
  
  if (costPerGallon && costPerGallon > 0) {
    weeklyCost = parseFloat((weeklyGallons * costPerGallon).toFixed(2))
    monthlyCost = parseFloat((weeklyCost * 4.33).toFixed(2)) // 4.33 weeks per month average
    
    const seasonWeeks = currentSeason === 'spring' ? 13 : 
                       currentSeason === 'summer' ? 13 :
                       currentSeason === 'fall' ? 13 : 13
    seasonalCost = parseFloat((weeklyCost * seasonWeeks).toFixed(2))
  }
  
  // Seasonal advice
  let seasonalAdvice = ""
  if (grass.season === 'Cool') {
    switch (currentSeason) {
      case 'spring':
        seasonalAdvice = "Spring is the optimal time for cool-season grass growth. Increase watering as temperatures rise and growth accelerates."
        break
      case 'summer':
        seasonalAdvice = "Summer stress requires careful watering. Water deeply and less frequently to encourage deep root growth and drought tolerance."
        break
      case 'fall':
        seasonalAdvice = "Fall watering prepares grass for winter. Maintain consistent moisture as growth slows and dormancy approaches."
        break
      case 'winter':
        seasonalAdvice = "Winter watering is minimal for cool-season grasses. Only water if there's been no precipitation for 2+ weeks."
        break
    }
  } else { // Warm season
    switch (currentSeason) {
      case 'spring':
        seasonalAdvice = "Spring watering helps warm-season grasses break dormancy. Increase frequency as soil temperatures rise above 65°F."
        break
      case 'summer':
        seasonalAdvice = "Summer is peak growing season for warm-season grasses. Maintain consistent moisture for optimal growth and health."
        break
      case 'fall':
        seasonalAdvice = "Fall watering prepares warm-season grasses for dormancy. Reduce frequency as growth slows."
        break
      case 'winter':
        seasonalAdvice = "Winter watering is not needed for warm-season grasses as they are dormant."
        break
    }
  }
  
  // Water conservation tips
  const waterConservationTips = [
    'Water early in the morning (6-8 AM) to reduce evaporation',
    'Use a rain gauge to monitor natural precipitation',
    'Adjust watering based on rainfall - skip sessions after 0.5"+ rain',
    'Consider installing a smart irrigation controller',
    'Use mulch around landscape beds to retain soil moisture',
    'Regularly check for and repair irrigation leaks'
  ]
  
  // System optimization tips
  const systemOptimizationTips = [
    applicationRateSource === 'system-estimate'
      ? `Your ${irrigationSystemName} estimate uses ${roundRate(defaultEffectiveRate)} inches per hour after ${(irrigationSystem.efficiency * 100).toFixed(0)}% efficiency`
      : `Your catch-can test is using ${applicationRateInchesPerHour} inches per hour, replacing the built-in ${roundRate(defaultEffectiveRate)} inches per hour system estimate`,
    'Ensure sprinkler heads are properly aligned and not blocked',
    'Check for uniform coverage - adjust heads if needed',
    'Replace worn nozzles for better water distribution',
    'Consider upgrading to water-efficient nozzles',
    'Install a moisture sensor to prevent overwatering'
  ]
  
  // Special considerations
  let specialConsiderations = [...grass.wateringNeeds.specialConsiderations]
  
  if (waterRestrictions) {
    specialConsiderations.push(
      'Check local water restrictions and adjust schedule accordingly',
      'Consider drought-tolerant grass alternatives for future renovations'
    )
  }

  if (zoneCount > 1) {
    specialConsiderations.push(
      'Zone runtimes assume each controller station waters a separate lawn area. If two stations water the same grass from different sides, use catch-can measurements for that overlap or split the target runtime between those stations.'
    )
  }

  if (cycleAndSoak.recommended) {
    specialConsiderations.push(
      `Use cycle and soak: run about ${cycleAndSoak.runMinutesPerCycle} minutes per cycle, wait ${cycleAndSoak.soakMinutes} minutes, then repeat for ${cycleAndSoak.cycles} total cycles.`
    )
  }
  
  if (soilType === 'sandy') {
    specialConsiderations.push(
      'Sandy soil drains quickly - consider more frequent, shorter sessions',
      'Add organic matter to improve water retention'
    )
  } else if (soilType === 'clay') {
    specialConsiderations.push(
      'Clay soil holds water longer - avoid overwatering',
      'Improve drainage with aeration and organic matter'
    )
  }
  
  return attachToolEngineMetadata({
    grassType: grass.name,
    lawnSize: canonicalLawnSize,
    zoneCount,
    averageZoneSize: parseFloat(averageZoneSize.toFixed(0)),
    soilType,
    irrigationSystem: irrigationSystem.name,
    season: currentSeason,
    weeklyWaterNeeded: parseFloat(weeklyWaterNeeded.toFixed(2)),
    soilAdjustedWater: parseFloat(soilAdjustedWater.toFixed(2)),
    weeklyGallons: parseFloat(weeklyGallons.toFixed(0)),
    weeklyGallonsPerZone: parseFloat(weeklyGallonsPerZone.toFixed(0)),
    gallonsPerZonePerSession: parseFloat(gallonsPerZonePerSession.toFixed(0)),
    targetInchesPerSession: parseFloat(targetInchesPerSession.toFixed(2)),
    defaultApplicationRateInchesPerHour: roundRate(defaultEffectiveRate),
    applicationRateInchesPerHour,
    applicationRateSource,
    catchCanTestMinutes: applicationRateSource === 'system-estimate' ? undefined : catchCanTestMinutes,
    rainfallSubtracted: parseFloat(rainfallSubtracted.toFixed(2)),
    skipThisWeek,
    irrigationFrequency,
    sessionDuration: roundedSessionDuration,
    sessionsPerWeek,
    controllerRunMinutesPerSession: parseFloat(controllerRunMinutesPerSession.toFixed(1)),
    controllerRunMinutesPerWeek: parseFloat(controllerRunMinutesPerWeek.toFixed(1)),
    zoneRunTimes,
    cycleAndSoak,
    weeklyCost,
    monthlyCost,
    seasonalCost,
    seasonalAdvice,
    waterConservationTips,
    systemOptimizationTips,
    specialConsiderations
  }, context)
}
